import {
    Box,
    Button,
    Container,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { ListCommunityPostsResponse } from '../api/communityApi';
import { CommunityPost, PostCategory } from '../database/community';
import PostSubmissionForm from './components/post/PostSubmissionForm';
import SocialBar from '../socials/SocialBar';
import { TabContext, TabPanel } from '@mui/lab';
import PostTab from './components/post/PostTab';
import { Lock } from '@mui/icons-material';
import { SubscriptionStatus, User } from '../database/user';
import SubscriptionPriceMatrix from '../subscription/SubscriptionPriceMatrix';
import { useLocation, useNavigate } from 'react-router';

interface GeneralizedPostTabProps {
    artist: User;
    user?: User;
    requiresSubscription: boolean;
    artistPostsOnly: boolean;
    postCategory: PostCategory;
    linkedPostId?: string;
    onClearLinkedPost?: () => void;
}

const GeneralizedPostTab: React.FC<GeneralizedPostTabProps> = ({
    artist,
    user,
    requiresSubscription,
    artistPostsOnly,
    postCategory,
    linkedPostId,
    onClearLinkedPost,
}) => {
    const [data, setData] = useState<ListCommunityPostsResponse>();
    // const [lastStartKey, setLastStartKey] = useState('');
    const [value, setValue] = useState('recent');
    const location = useLocation();
    const navigate = useNavigate();

    const onCreatePost = (post: CommunityPost) => {
        const newData = data?.posts || [];
        setData({
            posts: [post, ...newData],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const canViewContent =
        !requiresSubscription ||
        (user &&
            (user.isArtist ||
                user.subscriptionOverride ||
                user.subscriptionStatus === SubscriptionStatus.SUBSCRIBED));

    const shouldShowSubscriptionGate = !canViewContent;

    if (shouldShowSubscriptionGate) {
        return <SubscriptionPriceMatrix user={user} redirectPath={location.pathname} />;
    }

    if (
        user &&
        user.isArtist &&
        requiresSubscription &&
        !user.artistInfo?.subscriptionSettings?.activePriceId
    ) {
        return (
            <Stack alignItems='center' p={10}>
                <Typography textAlign='center' variant='h6' p={10}>
                    You need to set up the subscription in your profile to activate this
                    feature.
                </Typography>
                <Button
                    id='edit-profile-button'
                    variant='contained'
                    onClick={() => navigate('/profile/edit')}
                >
                    Edit Profile
                </Button>
            </Stack>
        );
    }

    return (
        <Container maxWidth='lg' sx={{ pt: 6, mb: '100px' }} disableGutters>
            <Stack spacing={4}>
                {(!artistPostsOnly || (artistPostsOnly && user?.isArtist)) && (
                    <PostSubmissionForm
                        onSubmit={(e) => onCreatePost(e)}
                        category={postCategory}
                    />
                )}
                <Box sx={{ width: '100%', typography: 'body1', mt: 5 }}>
                    <TabContext value={value}>
                        <Tabs
                            value={value}
                            onChange={(_, t) => setValue(t)}
                            aria-label='post tabs'
                            textColor='inherit'
                            centered
                        >
                            <Tab label='Most Recent' value='recent' />
                            {!artistPostsOnly &&
                                (!user ? (
                                    <Tooltip title='Sign in or up to view your posts'>
                                        <span>
                                            <Tab
                                                label='My Posts'
                                                value='my'
                                                disabled
                                                icon={<Lock />}
                                                iconPosition='start'
                                            />
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tab label='My Posts' value='my' />
                                ))}
                        </Tabs>
                        <TabPanel value='recent' sx={{ px: { xs: 0, sm: 3 } }}>
                            <PostTab
                                query={value}
                                data={data}
                                setData={setData}
                                category={postCategory}
                                linkedPostId={
                                    value === 'recent' ? linkedPostId : undefined
                                }
                                onClearLinkedPost={onClearLinkedPost}
                            />
                        </TabPanel>
                        <TabPanel value='my' sx={{ px: { xs: 0, sm: 3 } }}>
                            <PostTab
                                query={value}
                                data={data}
                                setData={setData}
                                category={postCategory}
                                linkedPostId={value === 'my' ? linkedPostId : undefined}
                                onClearLinkedPost={onClearLinkedPost}
                            />
                        </TabPanel>
                    </TabContext>
                </Box>
            </Stack>
            <SocialBar />
        </Container>
    );
};

export default GeneralizedPostTab;
